import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import PageHeader from '@features/PageHeader';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import AddDropdownHeader from '@components/AddDropdownHeader';
import { useTranslation } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { alertActions, knowledgeBaseActions, agentsActions } from '@actions';
import { knowledgeBaseService } from '@services';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { handleApiError } from '@helpers/api';
import { everyAgentAtLimit } from '@helpers/knowledge-base';
import {
    KNOWLEDGE_BASE_LIMIT_TOOLTIPS,
    KNOWLEDGE_BASE_TYPE_LABELS,
    KnowledgeBaseType,
    KnowledgeDocumentStatus,
} from '@constants/knowledge-base';
import { ChatbotHeaderIcon } from 'assets/images/icons';
import EditModal, { FORM_TYPE } from './EditModal';
import KnowledgeBaseTable from './KnowledgeBaseTable';
import FiltersPanel from './FiltersPanel';
import BulkAssignModal from './BulkAssignModal';

const REFRESH_INTERVAL = 5000;

const StyledFiltersPanel = styled(FiltersPanel)({
    '& svg': {
        height: 'auto',
        width: 'auto',
    },
});

const KnowledgeBase = () => {
    const { t } = useTranslation();

    const [selectedContent, setSelectedContent] = useState(null);
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [filter, setFilter] = useState({
        agents: [],
        types: [],
    });
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState({ list: {}, all: false });
    const [isBulkAssignOpen, setBulkAssignOpen] = useState(false);

    const { hasFullAccess } = usePermissions();
    const canManageContent = hasFullAccess('messaging');
    const locations = useSelector(state => state.account?.account?.companies || []);
    const totalCount = useSelector(state => state.knowledgeBase.totalCount);
    const items = useSelector(state => state.knowledgeBase.data);
    const agents = useSelector(state => state.agents.data);
    const dispatch = useDispatch();

    const selectedLength = Object.keys(selectedItems.list).length;
    const isSelecting = selectedLength > 0 || selectedItems.all;
    const hasProcessingDocument = items.some(item => item.status === KnowledgeDocumentStatus.PROCESSING);

    const currentQuery = useMemo(() => {
        return {
            agents: filter.agents,
            types: filter.types,
        };
    }, [filter]);

    const addOptions = Object.values(KnowledgeBaseType).map(type => {
        const atLimit = everyAgentAtLimit(agents, type);
        return {
            value: type,
            label: KNOWLEDGE_BASE_TYPE_LABELS[type],
            disabled: atLimit,
            tooltip: atLimit ? t(KNOWLEDGE_BASE_LIMIT_TOOLTIPS[type]) : undefined,
        };
    });

    useEffect(() => {
        dispatch(agentsActions.get());
        dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage));
    }, []);

    useEffect(() => {
        if (!hasProcessingDocument || modal.open || isSelecting) return undefined;
        const timer = setInterval(() => dispatch(knowledgeBaseActions.refresh()), REFRESH_INTERVAL);
        return () => clearInterval(timer);
    }, [hasProcessingDocument, modal.open, isSelecting]);

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(knowledgeBaseActions.get(currentQuery, newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(knowledgeBaseActions.get(currentQuery, 0, parseInt(event.target.value, 10)));
    };

    const onDelete = async () => {
        try {
            await knowledgeBaseService.deleteKnowledgeBase(selectedContent.id);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            dispatch(agentsActions.get());
            if (page === 0) {
                await dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage));
            } else {
                setPage(0); // INFO:Triggers refetch of templates
            }
            setDeleteModalOpen(false);
            setSelectedContent(null);
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
    };

    const onAddContent = contentType => setModal({ open: true, type: FORM_TYPE.NEW, contentType });

    const onApplyFilters = () => {
        dispatch(knowledgeBaseActions.get(currentQuery, 0, rowsPerPage));
        setPage(0);
    };

    return (
        <>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<ChatbotHeaderIcon />}
                title={t('KnowledgeBase.title')}
                subtitle={t('KnowledgeBase.subtitle')}
            >
                {canManageContent && (
                    <AddDropdownHeader
                        options={addOptions}
                        onAdd={onAddContent}
                        buttonText={t('buttons.addContent')}
                        buttonProps={{ variant: 'contained' }}
                    />
                )}
                <StyledFiltersPanel
                    onApplyFilters={onApplyFilters}
                    filter={filter}
                    setFilter={setFilter}
                    withChips={false}
                    type="knowledge-base"
                />
                {canManageContent && !!selectedLength && (
                    <Button variant="text" onClick={() => setBulkAssignOpen(true)}>
                        {t('buttons.assignAgents')}
                    </Button>
                )}
            </PageHeader>
            <KnowledgeBaseTable
                setModal={setModal}
                setSelectedContent={setSelectedContent}
                page={page}
                onPageChange={onPageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={onRowsPerPageChange}
                setDeleteModalOpen={setDeleteModalOpen}
                locations={locations}
                canWrite={canManageContent}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
            />
            {modal.open ? (
                <EditModal
                    selectedContent={selectedContent}
                    setSelectedContent={setSelectedContent}
                    currentQuery={currentQuery}
                    modal={modal}
                    setModal={setModal}
                    setPage={setPage}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    setDeleteModalOpen={setDeleteModalOpen}
                />
            ) : null}
            {isBulkAssignOpen && (
                <BulkAssignModal
                    isOpen={isBulkAssignOpen}
                    onClose={() => setBulkAssignOpen(false)}
                    selectedItems={selectedItems}
                    totalCount={totalCount}
                    currentQuery={currentQuery}
                    onAssigned={async () => {
                        dispatch(alertActions.success(t('alertMessages.editSuccess')));
                        dispatch(agentsActions.get());
                        await dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage));
                    }}
                />
            )}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </>
    );
};

export default KnowledgeBase;
